import {
  Text,
  ActionIcon,
  Autocomplete,
  Checkbox,
  Flex,
  Group,
  NumberInput,
  Select,
  Space,
  Stack,
  Box,
} from '@mantine/core';
import { FC, useContext, useState } from 'react';
import { IconDragDrop, IconDragDrop2, IconTrashX } from '@tabler/icons';
import { useDrag, useDrop } from 'react-dnd-latest';
import {
  BaseDocument,
  DiscountType,
  DragItem,
  PositionDragItem,
  PositionDto,
  PositionHandlers,
} from '../../../Service/DocumentT';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import { DataS } from '../../../Service/DataS';
import { TestS } from '../../../TestS';
import BookitupSegmentedControl from '../../../Atoms/BookitupSegmentedControl';
import { FormatS } from '../../../Service/FormatS';
import { EventRichInput, RichInput } from '../../../Organismns/tiptap/TiptapEditorInput';
import ResourcePositionAssignment from '../../../Organismns/Settings/Documents/Positions/ResourcePositionAssignment';
import BookingEvent from '../../../Types/Event';
import { useEventScope } from '../../../Service/hooks/useEventScope';
import DocumentBubbleMenu from '../../../Organismns/tiptap/toolbars/BubbleMenu';
import { DocumentPositionActions } from './PositionActions';
import PositionAmountInput from './PositionAmountInput';
import { PositionPrev } from './PositionPreview';
import { openAddProductModal } from './AddProductModal';
import { ResourceAddonContext } from '../../../Service/Context/ResourceAddonsContext';
import { DocumentService } from '../../../Service/helper/DocumentService';
import { TiptapS } from '../../../Service/TiptapS';
import { useElementSize } from '@mantine/hooks';
import { ThemeText } from '../../../Atoms/ThemeText';
import LinkText from '../../../Atoms/LinkText';
import { ViolationContext } from '../../../Service/Context/ViolationContext';
import { LicenceS } from '../../../Service/LicenceS';

interface Props {
  position: PositionDto;
  bookitupDocument: BaseDocument;
  handlers: PositionHandlers;
  products: PositionDto[];
  event?: BookingEvent;
  previewVersion?: boolean;
}

const DocumentPosition: FC<Props> = ({
  position,
  bookitupDocument,
  handlers,
  products,
  event,
  previewVersion = false,
}) => {
  const { profile } = useContext(ProfileContext);
  const [resourcePresent, setResourcePresent] = useState(false);
  const { isEventScope, eventId } = useEventScope();
  const { patchPosition, addPosition, deletePosition, movePosition, addProducts } = handlers;
  const { isActive } = useContext(ResourceAddonContext);

  const { taxType, documentType, positionGroups, currency, currencySymbol } = bookitupDocument;
  const {
    discount,
    id: positionId,
    label,
    subtext,
    price,
    tax,
    netPrice,
    priceUnit,
    netPriceUnit,
    unit,
    amount,
    optional,
    autoSelect,
    positionType,
  } = position;
  const { value: discountValue, type: discountType } = discount ?? { amount: 0, type: 'PERCENT' };
  const isPartialInvoice = documentType === 'PARTIAL_INVOICE';
  const isSmallScaleBusiness = profile && profile.smallScaleBusiness;
  const { ref, width } = useElementSize();
  const { executeWithLicenceCheck } = useContext(ViolationContext);

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: DragItem.POSITION,
      canDrop: (item: PositionDragItem) => item.position.id !== position.id,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: !!monitor.canDrop(),
      }),
      drop(item) {
        movePosition(item.position.id, position.id);
      },
    }),
    [position.id],
  );

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DragItem.POSITION,
      item: { position },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
        handlerId: monitor.getHandlerId(),
      }),
    }),
    [position.id],
  );

  const addProduct = () =>
    executeWithLicenceCheck(LicenceS.Restrictions.PRODUCT_USAGE, () => {
      openAddProductModal({ products, onFinish: (p) => addProducts(p, positionId) });
    });

  const opacity = isDragging ? 0.4 : 1;

  if (previewVersion) {
    return (
      <>
        <Box
          pos="relative"
          sx={(theme) => ({
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[0],
            border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[1]}`,
            padding: 10,
            borderRadius: 6,
          })}
        >
          <PositionPrev
            document={bookitupDocument as any}
            taxType={taxType as any}
            favoritePosition={false}
            position={position}
          />
          <div style={{ position: 'absolute', top: 5, right: 5 }}>
            <DocumentPositionActions
              previewVersion
              position={position}
              positionHandlers={handlers}
              isPartialInvoice={isPartialInvoice}
              positionGroups={positionGroups}
            />
          </div>
        </Box>
        {documentType !== 'PARTIAL_INVOICE' && (
          <Group>
            <LinkText mt={-5} onClick={() => addPosition(positionId)}>
              + Leere Position einfügen
            </LinkText>
            <LinkText mt={-5} onClick={addProduct}>
              + Produkt auswählen
            </LinkText>
          </Group>
        )}
      </>
    );
  }

  return (
    <div ref={ref}>
      <Group noWrap ref={drop} spacing={5}>
        {!isPartialInvoice && (
          <div style={{ width: 30 }}>
            <ActionIcon ref={drag}>
              {canDrop && <IconDragDrop color="#1D5191" size={isOver ? 28 : 20} />}
              {!canDrop && <IconDragDrop2 size={20} style={{ cursor: 'grab' }} />}
            </ActionIcon>
          </div>
        )}
        {isPartialInvoice && <div style={{ width: 20 }} />}
        <Stack
          p="xs"
          spacing="xs"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
            color: theme.colorScheme === 'dark' ? 'white' : 'black',
            borderRadius: 10,
            opacity,
          })}
        >
          <Group spacing="xs">
            <div style={{ width: width - (isPartialInvoice ? 495 : 640) }}>
              {isEventScope && (
                <EventRichInput
                  value={label}
                  patchField={(val) => patchPosition(positionId, { label: val })}
                  menu={<DocumentBubbleMenu />}
                />
              )}
              {!isEventScope && (
                <RichInput
                  value={label}
                  patchField={(val) => patchPosition(positionId, { label: val })}
                  menu={<DocumentBubbleMenu />}
                />
              )}
            </div>
            {positionType !== 'FREE_TEXT' && (
              <>
                <PositionAmountInput position={position} onChange={(patch) => patchPosition(positionId, patch)} />
                <Autocomplete
                  defaultValue={unit ?? ''}
                  data={DataS.positionUnitSuggestions}
                  onBlur={(e) => patchPosition(positionId, { unit: e.currentTarget.value })}
                  sx={{ width: 60 }}
                />
                <NumberInput
                  hideControls
                  decimalSeparator=","
                  precision={2}
                  removeTrailingZeros
                  value={taxType === 'gross' ? priceUnit : netPriceUnit}
                  sx={{ width: 70 }}
                  onBlur={(e) => {
                    const tmp = FormatS.formatAsCurrency(e.currentTarget.value);
                    patchPosition(positionId, {
                      priceUnit: taxType === 'gross' ? tmp : DocumentService.calculateGrossPrice(tmp, tax, true),
                    });
                  }}
                  data-testid={TestS.Documents.Edit.Position.DISCOUNT_VALUE}
                />
                {!isSmallScaleBusiness && (
                  <Select
                    value={tax.toString()}
                    data={DataS.getTaxRateOptions(currency, tax)}
                    sx={{ width: 55 }}
                    onChange={(val) => patchPosition(positionId, { tax: Number(val) })}
                    // eslint-disable-next-line react/jsx-no-useless-fragment
                    rightSection={<></>}
                    rightSectionWidth={0}
                  />
                )}
                {!isPartialInvoice && (
                  <>
                    <NumberInput
                      hideControls
                      min={0}
                      decimalSeparator=","
                      precision={Number.isInteger(discountValue) ? 0 : 2}
                      value={discountValue}
                      sx={{ width: 60 }}
                      onBlur={(e) =>
                        patchPosition(positionId, { discountValue: FormatS.formatAsCurrency(e.currentTarget.value) })
                      }
                      data-testid={TestS.Documents.Edit.Position.DISCOUNT_VALUE}
                    />
                    <BookitupSegmentedControl
                      value={discountType}
                      data={DataS.getDiscountUnitOptions(currencySymbol)}
                      onChange={(val: DiscountType) => val && patchPosition(positionId, { discountType: val })}
                      sx={{ marginLeft: -10 }}
                      testId={TestS.Documents.Edit.Position.DISCOUNT_TYPE}
                    />
                  </>
                )}
                <Text align="center" sx={{ width: 115 }}>
                  {FormatS.formatPriceWithCurrency(currency, taxType === 'net' ? netPrice : price)}
                </Text>
              </>
            )}
          </Group>
          <div style={{ width: width - 95 }}>
            {isEventScope && (
              <EventRichInput
                value={subtext}
                menu={<DocumentBubbleMenu />}
                patchField={(val) => patchPosition(positionId, { subtext: TiptapS.convertParagraphsToBreaks(val) })}
              />
            )}
            {!isEventScope && (
              <RichInput
                value={subtext}
                menu={<DocumentBubbleMenu />}
                patchField={(val) => patchPosition(positionId, { subtext: TiptapS.convertParagraphsToBreaks(val) })}
              />
            )}
          </div>
          <Group position="apart">
            {isActive && positionType !== 'FREE_TEXT' && (
              <ResourcePositionAssignment
                position={position}
                favoritePosition={false}
                eventId={isEventScope ? Number(eventId) : undefined}
                amount={amount}
                setResourcePresent={setResourcePresent}
              />
            )}
            {!(isActive && positionType === 'FREE_TEXT') && <div />}
            <Group>
              {/* {resourcePresent && positionType !== 'FREE_TEXT' && (
                <Checkbox
                  size="xs"
                  label={
                    <Text color="dimmed" size="xs">
                      Ressourceninhalt anzeigen
                    </Text>
                  }
                  checked={positionType === 'RESOURCE'}
                  onChange={() =>
                    patchPosition(positionId, { positionType: positionType === 'RESOURCE' ? 'REGULAR' : 'RESOURCE' })
                  }
                />
              )} */}
              {/* {!resourcePresent && positionType !== 'RESOURCE' && (
                <Checkbox
                  size="xs"
                  label={
                    <Text color="dimmed" size="xs">
                      Freitext Position
                    </Text>
                  }
                  checked={positionType === 'FREE_TEXT'}
                  onChange={() =>
                    patchPosition(positionId, { positionType: positionType === 'FREE_TEXT' ? 'REGULAR' : 'FREE_TEXT' })
                  }
                />
              )} */}
              {documentType === 'OFFER' && positionType !== 'FREE_TEXT' && (
                <Checkbox
                  size="xs"
                  label={
                    <Text color="dimmed" size="xs">
                      optional
                    </Text>
                  }
                  checked={optional}
                  onChange={() => patchPosition(positionId, { optional: !optional })}
                />
              )}
              {optional && (
                <Checkbox
                  size="xs"
                  label={
                    <Text color="dimmed" size="xs">
                      Option vorausgewählt
                    </Text>
                  }
                  checked={autoSelect}
                  onChange={() => patchPosition(positionId, { autoSelect: !autoSelect })}
                />
              )}
            </Group>
          </Group>
        </Stack>
        <Stack justify="space-between">
          <DocumentPositionActions position={position} positionHandlers={handlers} positionGroups={positionGroups} />
          <Space h={40} />
          <ActionIcon onClick={() => deletePosition(positionId)}>
            <IconTrashX size={20} />
          </ActionIcon>
        </Stack>
      </Group>
      {documentType !== 'PARTIAL_INVOICE' && (
        <Group mt="md">
          <LinkText
            mt={-5}
            ml={30}
            color="blue"
            variant="link"
            sx={{ cursor: 'pointer', display: 'inline-block' }}
            onClick={() => addPosition(positionId)}
          >
            + Leere Position einfügen
          </LinkText>
          <LinkText
            mt={-5}
            color="blue"
            variant="link"
            sx={{ cursor: 'pointer', display: 'inline-block' }}
            onClick={addProduct}
          >
            + Produkt auswählen
          </LinkText>
        </Group>
      )}
    </div>
  );
};

export default DocumentPosition;
